import {useCallback, useEffect, useRef, useState} from "react";
import {BreadcrumbsType} from "@/app/components/breadcrumbs/widget";
import {NearbyUsersModel, NewUserModel, SkillLevelEnum, UserRequests} from "@/app/api/user";
import {CourtBigModel, CourtsRequests} from "@/app/api/courts";
import Cookies from "js-cookie";
import {CookiesKey} from "@/app/utils/cookies";
import {route} from "@/app/routes/AppRoutes";
import {useNavigate} from "react-router-dom";
import {InvitationRequests, SendInvitationRequestModel} from "@/app/api/invitation";
import {ReviewModel, ReviewsRequests} from "@/app/api/reviews";
import {
  OpenMatchModel,
  OpenMatchRequests,
  OpenMatchStatusEnum,
  SkillLevelOpenMatchEnum,
  TimeRangeEnum
} from "@/app/api/open_match";
import { logEventSafe } from "@/app/utils/analytics";

interface ScreenState {
  hasError?: string;
  hasLoading: boolean;
  isCreatingMatch: boolean;
  items?: Array<BreadcrumbsType | string>;
  users?: NearbyUsersModel[];
  location?: CourtBigModel;
  showSelectMap: boolean
  showSelectUser: boolean
  reviews?: ReviewModel[]
  timeRange?: TimeRangeEnum
  skillLevel?: SkillLevelOpenMatchEnum
  eventDate?: Date
  commentMessage?: string
}

const useCreateAMatch = () => {
  const navigate = useNavigate();

  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    hasLoading: true,
    isCreatingMatch: false,
    items: [BreadcrumbsType.padel, BreadcrumbsType.createAMatch],
    users: [],
    location: undefined,
    showSelectMap: false,
    showSelectUser: false,
    reviews: [],
    timeRange: undefined,
    skillLevel: undefined,
    eventDate: new Date(),
    commentMessage: ''
  });

  // Guard to skip cookie writes on the very first mount until initial hydration is complete
  const isInitialized = useRef(false);

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  useEffect(() => {
    const hydrate = async () => {
      updateState({hasLoading: true});
      const users = Cookies.get(CookiesKey.users);
      const locationStr = Cookies.get(CookiesKey.location);

      const reviewsStr = Cookies.get(CookiesKey.reviews);
      const timeRange = Cookies.get(CookiesKey.timeRange);
      const skillLevel = Cookies.get(CookiesKey.skillLevel);
      const eventDate = Cookies.get(CookiesKey.eventDate);
      const commentMessage = Cookies.get(CookiesKey.commentMessage);

      if (users !== undefined) {
        updateState({users: users ? JSON.parse(users) : []});
      }

      // Default: load reviews from cookie if present
      if (reviewsStr !== undefined) {
        updateState({reviews: reviewsStr ? JSON.parse(reviewsStr) : []});
      }

      // Robust location hydration
      if (locationStr !== undefined) {
        try {
          const parsed = locationStr ? JSON.parse(locationStr) : undefined;
          if (parsed?.courtId) {
            // Fetch full court and reviews by id
            const court = await CourtsRequests.getCourt({ courtId: parsed.courtId });
            const courtReviews = await ReviewsRequests.getCourtReview({ courtId: parsed.courtId });
            updateState({ location: court, reviews: courtReviews });
          } else if (parsed?.id) {
            // Backward compatibility if full object is stored
            updateState({ location: parsed });
          } else {
            updateState({ location: undefined });
          }
        } catch {
          updateState({ location: undefined });
        }
      }

      if (timeRange !== undefined && timeRange) {
        updateState({ timeRange: timeRange as TimeRangeEnum });
      }
      if (skillLevel !== undefined && skillLevel) {
        updateState({ skillLevel: skillLevel as SkillLevelOpenMatchEnum });
      }
      if (eventDate !== undefined && eventDate) {
        updateState({ eventDate: new Date(eventDate) });
      }
      if (commentMessage !== undefined) {
        updateState({ commentMessage: commentMessage || '' });
      }

      updateState({hasLoading: false});
      // Mark as initialized after we've attempted to hydrate from cookies
      isInitialized.current = true;
    };

    hydrate();
  }, [updateState]);

  useEffect(() => {
    const usersFromCookie = Cookies.get(CookiesKey.users);
    if (usersFromCookie !== state.users) {
      Cookies.set(CookiesKey.users, JSON.stringify(state.users));
    }
  }, [state.users]);

  useEffect(() => {
    if (!isInitialized.current) return;
    if (state.timeRange !== undefined) {
      Cookies.set(CookiesKey.timeRange, state.timeRange);
    } else {
      Cookies.remove(CookiesKey.timeRange);
    }
  }, [state.timeRange]);

  useEffect(() => {
    if (!isInitialized.current) return;
    if (state.skillLevel !== undefined) {
      Cookies.set(CookiesKey.skillLevel, state.skillLevel);
    } else {
      Cookies.remove(CookiesKey.skillLevel);
    }
  }, [state.skillLevel]);

  useEffect(() => {
    if (!isInitialized.current) return;
    if (state.eventDate) {
      const iso = new Date(state.eventDate).toISOString();
      Cookies.set(CookiesKey.eventDate, iso);
    } else {
      Cookies.remove(CookiesKey.eventDate);
    }
  }, [state.eventDate]);

  useEffect(() => {
    if (!isInitialized.current) return;
    // Persist even empty strings; we only clean up after a successful match creation
    Cookies.set(CookiesKey.commentMessage, state.commentMessage ?? '');
  }, [state.commentMessage]);

  const toggleSelectUser = useCallback(() => {
    updateState({showSelectUser: !state.showSelectUser});
  }, [state.showSelectUser, updateState]);

  const selectUser = useCallback((user: NearbyUsersModel | undefined) => {
    if (user === undefined) return;
    if (state.users?.find((u) => u.id === user.id)) {
      updateState({users: state.users?.filter((u) => u.id !== user.id)});
      toggleSelectUser();
    } else {
      updateState({users: [...state?.users ?? [], user]});
      toggleSelectUser();
    }
  }, [toggleSelectUser, updateState]);

  const goToSelectMap = useCallback(() => {
    navigate(route.maps);
  }, [updateState]);

  const createMatch = useCallback(async () => {
    if (state.users?.length === 0 || state.location?.id === undefined) return;
    const selectedUserIds = state.users?.map((u) => u.id);
    if (selectedUserIds === undefined) return;
    
    updateState({ isCreatingMatch: true });
    
    try {
      const owner = await UserRequests.getCurrentUser();

      // Step 1: Create match with only owner's ID
      const reqData: OpenMatchModel = {
        courtId: state?.location?.id,
        ownerId: owner.user.id,
        userIds: [owner.user.id], // Only include owner's ID initially
        status: OpenMatchStatusEnum.NOT_STARTED,
        eventDate: state.eventDate,
        timeRanges: state.timeRange,
        skillLevel: state.skillLevel,
        commentMessage: state.commentMessage || ''
      }

      const created = await OpenMatchRequests.createMatch(reqData);

      // Resolve matchId and eventDate for redirect, even if backend returns no body
      let matchId = created?.id;
      let eventDateForRedirect: Date | undefined = created?.eventDate || state.eventDate;

      if (!matchId && owner?.user?.id && state.location?.id) {
        try {
          for (let i = 0; i < 5 && !matchId; i++) {
            const candidates = await OpenMatchRequests.getMatches({ eventDate: state.eventDate });
            const found = candidates.find(m =>
              m.owner?.id === owner.user.id &&
              m.court?.id === state.location?.id &&
              (state.timeRange ? m.timeRanges === state.timeRange : true) &&
              (state.skillLevel ? m.skillLevel === state.skillLevel : true) &&
              ((state.commentMessage ?? '') ? (m.commentMessage ?? '') === (state.commentMessage ?? '') : true)
            );
            if (found?.id) {
              matchId = found.id;
              eventDateForRedirect = found.eventDate || eventDateForRedirect;
              break;
            }
            if (i < 4) {
              await new Promise(r => setTimeout(r, 400));
            }
          }
        } catch {}
      }

      // Log match create
      if (matchId) {
        await logEventSafe('padel_match_create', {
          match_id: matchId,
          club_id: state.location?.id,
          skill_level: state.skillLevel,
          preferred_time: state.timeRange,
        });
      }

      // Step 2: Send invitations to all selected users after successful match creation
      if (matchId && state.location?.id) {
        // Send invitations to all selected users
        for (const userId of selectedUserIds) {
          try {
            const invitationModel: SendInvitationRequestModel = {
              toUserIds: [userId],
              courtId: state.location.id,
              openMatchId: matchId
            };
            await InvitationRequests.sendInvitation(invitationModel);
          } catch (invitationError) {
            console.error(`Failed to send invitation to user ${userId}:`, invitationError);
            // Continue sending other invitations even if one fails
          }
        }

        // Log invites sent aggregated
        await logEventSafe('padel_match_invite_sent', {
          match_id: matchId,
          invited_count: selectedUserIds.length,
        });

        // Navigate to match detail page after successful creation and invitations
        // Create query parameters with match details
        const params = new URLSearchParams();
        params.append('matchId', matchId.toString());

        // Add date parameter to narrow down the search
        if (eventDateForRedirect) {
          // Format date as YYYY-MM-DD to avoid LocalDate conversion issues on the server
          const date = new Date(eventDateForRedirect);
          const formattedDate = date.toISOString().split('T')[0]; // Get only the date part (YYYY-MM-DD)
          params.append('eventDate', formattedDate);
        }

        // Navigate to match detail page with query parameters
        // Clear persisted selections after successful creation
        Cookies.remove(CookiesKey.timeRange);
        Cookies.remove(CookiesKey.skillLevel);
        Cookies.remove(CookiesKey.eventDate);
        Cookies.remove(CookiesKey.commentMessage);
        Cookies.remove(CookiesKey.users);
        Cookies.remove(CookiesKey.location);
        Cookies.remove(CookiesKey.reviews);

        navigate(`${route.matchDetailPage}?${params.toString()}`);
      }
    } catch (e) {
      console.error('Failed to create match:', e);
      updateState({ hasError: 'Failed to create match. Please try again.' });
    } finally {
      updateState({ isCreatingMatch: false });
    }
  }, [state.users, state.location, state.eventDate, state.timeRange, state.skillLevel, state.commentMessage, updateState, navigate]);

  const onSelectTimeRange = useCallback((timeRange: TimeRangeEnum) => {
    updateState({timeRange});
  }, [updateState]);

  const onSelectSkillLevel = useCallback((skillLevel: SkillLevelOpenMatchEnum) => {
    updateState({skillLevel});
  }, [updateState]);

  const onSelectEventDate = useCallback((eventDate: Date) => {
    updateState({eventDate});
  }, [updateState]);

  const onCommentMessageChange = useCallback((commentMessage: string) => {
    // Avoid redundant updates that can be triggered by the Input on mount
    if (state.commentMessage === commentMessage) return;
    updateState({commentMessage});
  }, [state.commentMessage, updateState]);

  const disabledCreateMatch = useCallback(() => {
    return !state.users || state.users.length === 0 || !state.location || state.location?.id === undefined || state.location?.id === null || state.timeRange === undefined || state.skillLevel === undefined || !state.eventDate || !state.commentMessage || state.commentMessage.trim() === '' ;
  }, [state.users, state.location, state.timeRange, state.skillLevel, state.eventDate, state.commentMessage]);

  return {
    state,
    toggleSelectUser,
    selectUser,
    goToSelectMap,
    createMatch,
    onSelectTimeRange,
    onSelectSkillLevel,
    onSelectEventDate,
    onCommentMessageChange,
    disabledCreateMatch,
    isCreatingMatch: state.isCreatingMatch
  };
};

export default useCreateAMatch;
